import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { logout } from "../../LoginPage/redux/auth.actions"
import { SETTING_SLICE_NAME } from "./setting.constants"
import {
  acceptInviteUser,
  createInviteUser,
  getInviteUserInfo,
  getLanguagesList,
  getUsersManagement,
  getUsersManagementRoles,
  navToStripe,
  rejectInviteUser,
  removeUser,
  resendInviteUser,
  updateInviteUser
} from "./setting.thunks"

const initialState = {
  languages: [],

  subscribe: null,

  saveResult: false,
  saveFetching: false,

  fetching: false,
  errors: false,

  lastLanguage: "",

  rolesFetching: false,
  roles: [],

  users: [],

  inviteInfoFetching: false,
  inviteInfo: null
}

const setSaveFetching = (state) => {
  state.saveFetching = true
}

const stopSaveFetching = (state) => {
  state.saveFetching = false
}

const slice = createSlice({
  name: SETTING_SLICE_NAME,
  initialState,
  reducers: {
    settingsSaveResultReset(state) {
      state.fetching = false
      state.saveFetching = false
      state.saveResult = null
    },
    settingsErrorsReset(state) {
      state.fetching = false
      state.saveFetching = false
      state.errors = false
    },
    clearInviteUserInfo(state) {
      state.inviteInfo = null
    },
    setLastLanguagePlanPage(state, action) {
      state.lastLanguage = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialState)

      /* ---------------- languages / stripe ---------------- */
      .addCase(getLanguagesList.pending, (state) => {
        state.fetching = true
      })
      .addCase(getLanguagesList.fulfilled, (state, action) => {
        state.fetching = false
        state.languages = action.payload
      })
      .addCase(getLanguagesList.rejected, (state, action) => {
        state.fetching = false
        state.errors = action.payload
      })
      .addCase(navToStripe.pending, (state) => {
        state.fetching = true
      })
      .addCase(navToStripe.fulfilled, (state) => {
        state.fetching = false
      })
      .addCase(navToStripe.rejected, (state) => {
        state.fetching = false
      })

      /* ---------------- users management ---------------- */
      .addCase(getUsersManagementRoles.pending, (state) => {
        state.rolesFetching = true
      })
      .addCase(getUsersManagementRoles.fulfilled, (state, action) => {
        state.rolesFetching = false
        state.roles = action.payload
      })
      .addCase(getUsersManagementRoles.rejected, (state) => {
        state.rolesFetching = false
      })
      .addCase(getUsersManagement.pending, (state) => {
        state.fetching = true
      })
      .addCase(getUsersManagement.fulfilled, (state, action) => {
        state.fetching = false
        state.users = action.payload
      })
      .addCase(getUsersManagement.rejected, (state) => {
        state.fetching = false
      })
      .addCase(createInviteUser.pending, setSaveFetching)
      .addCase(createInviteUser.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "invite_create"
      })
      .addCase(createInviteUser.rejected, stopSaveFetching)
      .addCase(updateInviteUser.pending, setSaveFetching)
      .addCase(updateInviteUser.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "invite_update"
      })
      .addCase(updateInviteUser.rejected, stopSaveFetching)
      .addCase(resendInviteUser.pending, setSaveFetching)
      .addCase(resendInviteUser.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "invite_resend"
      })
      .addCase(resendInviteUser.rejected, stopSaveFetching)
      .addCase(removeUser.pending, setSaveFetching)
      .addCase(removeUser.fulfilled, (state, action) => {
        const user = action.payload
        state.users = state.users.filter((el) => el.id !== (user?.id || user))
        state.saveFetching = false
        state.saveResult = "removed"
      })
      .addCase(removeUser.rejected, stopSaveFetching)
      .addCase(getInviteUserInfo.pending, (state) => {
        state.inviteInfoFetching = true
      })
      .addCase(getInviteUserInfo.fulfilled, (state, action) => {
        state.inviteInfoFetching = false
        state.inviteInfo = action.payload
      })
      .addCase(getInviteUserInfo.rejected, (state) => {
        state.inviteInfoFetching = false
      })
      .addCase(rejectInviteUser.pending, setSaveFetching)
      .addCase(rejectInviteUser.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "invite_reject"
        state.inviteInfo = null
      })
      .addCase(rejectInviteUser.rejected, stopSaveFetching)
      .addCase(acceptInviteUser.pending, setSaveFetching)
      .addCase(acceptInviteUser.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "invite_accept"
        state.inviteInfo = null
      })
      .addCase(acceptInviteUser.rejected, stopSaveFetching)
    // logoutInviteUser's request/success/failure reducers were all no-ops (`state => state`)
    // in the legacy reducer — intentionally unhandled here too.
  }
})

export const settingActions = slice.actions

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "setting",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
